/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LicenseCardModule */

import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';

import {
    ILicensePool,
    IOrgServiceUnits,
    LicenseTierType,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { AlertType } from 'ng/modules/licensing/licensing.types';

import './license-card-content.component.less';
import * as globalL10n from 'global-l10n';
import * as l10n from '../license-card.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component displaying license usage data for controller and organization.
 * @author alextsg, Shubam Bindal
 */
@Component({
    selector: 'license-card-content',
    templateUrl: './license-card-content.component.html',
})
export class LicenseCardContentComponent implements OnInit, OnChanges {
    /**
     * Available License service cores.
     */
    @Input()
    public availableServiceCores = 0;

    /**
     * Reserved License service cores.
     */
    @Input()
    public reservedServiceCores = 0;

    /**
     * Used service cores from total licensed service cores.
     */
    @Input()
    public usedServiceCores = 0;

    /**
     * License pool information from saasStatus for Pool usage.
     */
    @Input()
    public licensePool: ILicensePool | undefined;

    /**
     * Default license tier used.
     */
    @Input()
    public licenseTier: string;

    /**
     * True to show the currently configured license tier. Used by the administration dashboard
     * widget where the license tier is not shown in the header
     */
    @Input()
    public showLicenseTier = false;

    /**
     * True to show the current connectivity status. Used by the administration dashboard
     * widget where the license tier is not shown in the header
     */
    @Input()
    public showConnectivityStatus = false;

    /**
     * Default connectivity status used.
     */
    @Input()
    public connectivityStatus = false;

    public statusColor = '';

    public licenseTierType = '';

    public maxOrgUnitsValue = 0;

    public usedOrgUnitsValue = 0;

    public maxPoolUnitsValue = 0;

    public usedPoolUnitsValue = 0;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly enterpriseCloudServiceTier = LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES;

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    public get licensingConnectivityStatus(): AlertType {
        return this.connectivityStatus ? AlertType.success : AlertType.danger;
    }

    public get connectivityStatusText(): string {
        return this.connectivityStatus ? l10nKeys.connectedLabel : l10nKeys.disconnectedLabel;
    }

    /**
     * Organization-related licensing information.
     */
    @Input('orgServiceUnits')
    private set setOrgServiceUnits(orgServiceUnits: IOrgServiceUnits | undefined) {
        this.maxOrgUnitsValue = orgServiceUnits?.available_service_units ?? 0;
        this.usedOrgUnitsValue = orgServiceUnits?.used_service_units ?? 0;
    }

    public ngOnInit(): void {
        this.setTierTypeAndConnectivityStatus();
        this.updatePoolUsageValues();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.licensePool) {
            this.updatePoolUsageValues();
        }
    }

    /**
     * Update pool usage values based on licensePool.
     */
    private updatePoolUsageValues(): void {
        if (this.licensePool) {
            this.maxPoolUnitsValue = this.licensePool.available_service_units ?? 0;
            this.usedPoolUnitsValue = this.licensePool.used_service_units ?? 0;
        }
    }

    private setTierTypeAndConnectivityStatus(): void {
        if (this.licenseTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
            this.licenseTierType = this.l10nService.getMessage(this.l10nKeys.cloudLicensesLabel);
        } else if (this.licenseTier === LicenseTierType.ENTERPRISE) {
            this.licenseTierType = this.l10nService.getMessage(this.l10nKeys.onPremLicensesLabel);
        }
    }
}
